/**
 * Strategy-Delivery Cascade by Scope - Complete Enhanced Entry
 * The topmost organizing lens for all work
 * 
 * Core Question: "How does work flow from intent to impact?"
 */

import type { EnhancedFrameworkEntry } from '../../types/enhancedFramework';

export const strategyDeliveryCascade: EnhancedFrameworkEntry = {
  // ===== BASIC IDENTITY =====
  id: 'strategy-delivery-cascade',
  canonicalName: 'Strategy-Delivery Cascade by Scope',
  aliases: ['SDC×S', 'Strategy-Delivery Matrix', 'Intent-to-Impact Cascade'],
  
  // ===== TYPE CLASSIFICATION =====
  frameworkType: 'clarity-alignment',
  
  // ===== MULTI-RESOLUTION CONTENT =====
  content: {
    phrase: 'Systematic cascade from intent to impact',
    
    sentence: 'The Strategy-Delivery Cascade by Scope is a 4×2 matrix that organizes all work across four phases (Strategy, Prioritization, Planning, Delivery) and two scopes (Individual, Organization/Group)—with Prioritization as the spine connecting intent to impact.',
    
    paragraph: 'Most organizations struggle to connect strategic intent with actual results because work exists in scattered silos with no clear through-line. The Strategy-Delivery Cascade by Scope provides the master organizing lens that shows how all work flows through four essential phases—Strategy defines direction, Prioritization focuses energy, Planning structures commitments, and Delivery generates results and learning. Applied across two scopes (Individual and Organization/Group), this creates 8 distinct contexts where work happens. Prioritization acts as the critical spine, bridging the gap between strategic clarity and execution excellence.',
    
    fullPage: `# Strategy-Delivery Cascade by Scope

## The Core Question

**"How does work flow from intent to impact?"**

## Overview

The Strategy-Delivery Cascade by Scope is the topmost organizing lens for all work. It reveals how every piece of meaningful work flows through four connected phases, applied across two scopes, creating 8 distinct contexts where priorities are set and progress is made.

## The Problem It Solves

Most organizations suffer from a chronic disconnect:
- **Strategy** is set at the top but never reaches the front lines
- **Prioritization** happens in silos without strategic alignment
- **Planning** creates commitments that don't reflect true priorities
- **Delivery** produces results that don't connect back to strategy

The result? Wasted effort, misalignment, and the feeling that "we're busy but not making progress on what matters."

## The Four Phases (The Cascade Dimension)

### 1. Strategy — Define Direction

**What it is**: Establishes the "why" and "where" of organizational focus.

**Key outputs**:
- Shared context for what matters most
- North star that downstream decisions reference
- Strategic objectives and themes

**Common failure modes**:
- Too vague to guide decisions
- Changes too frequently
- Never communicated to execution teams

### 2. Prioritization — Focus Energy (THE SPINE)

**What it is**: The bridge between strategy and execution. The act of allocating finite attention, resources, and energy to what most advances strategic intent.

**Key outputs**:
- Focus list (what's above/below the line)
- Resource allocation decisions
- Tradeoff criteria and policies

**Why it's the spine**: 
Prioritization is the critical linking mechanism. Without it:
- Strategy remains theoretical
- Planning becomes a wish list
- Delivery drifts toward the urgent over the important

**Common failure modes**:
- Everything is a priority (nothing is)
- Political rather than strategic
- Done once per year instead of continuously

### 3. Planning — Structure Commitments

**What it is**: Converts prioritization into sequenced, coordinated commitments across teams.

**Key outputs**:
- Timelines and milestones
- Resource assignments
- Dependencies and sequencing
- Risk mitigation plans

**The connection**: Planning asks "Given our priorities, how do we coordinate action?"

**Common failure modes**:
- Plans ignore priorities
- Over-planning (creating false precision)
- Plans become rigid, not adaptive

### 4. Delivery — Generate Results & Learning

**What it is**: The "make it real" phase focused on execution rhythms, feedback loops, and learning.

**Key outputs**:
- Shipped value (features, outcomes, capabilities)
- Performance data
- Learning and adaptation signals
- Feedback to refine strategy and priorities

**The connection**: Delivery creates the evidence that validates (or invalidates) strategy and priorities.

**Common failure modes**:
- Optimized for output, not outcomes
- No feedback loops to strategy/priorities
- Celebrates completion, not impact

## The Two Scopes (The Scope Dimension)

### 1. Individual

Work at the personal or professional level:
- Individual strategy (career, growth, contribution)
- Personal prioritization (focus, energy, time)
- Individual planning (goals, projects, habits)
- Personal delivery (output, impact, learning)

### 2. Organization/Group

Work at the team, department, or organizational level:
- Organizational strategy (vision, positioning, objectives)
- Team prioritization (portfolio management, resource allocation)
- Organizational planning (roadmaps, programs, initiatives)
- Organizational delivery (shipped products, services, outcomes)

## The Matrix: 4 Phases × 2 Scopes = 8 Contexts

Each of the 8 cells represents a distinct context where work happens:

| Phase | Individual | Organization/Group |
|-------|-----------|-------------------|
| **Strategy** | Personal vision & direction | Organizational strategy |
| **Prioritization** | Personal focus & energy | Portfolio & resource allocation |
| **Planning** | Personal goals & projects | Roadmaps & initiatives |
| **Delivery** | Personal output & learning | Shipped value & outcomes |

## Prioritization as the Spine

**Why prioritization is special**: It's the only phase that cannot be skipped:

- **Without Strategy**: Prioritization can still happen (locally optimized)
- **Without Planning**: Prioritization can still guide action (emergent)
- **Without Delivery**: Prioritization still creates focus (even if unrealized)

**But without Prioritization**:
- Strategy has no mechanism to guide action
- Planning becomes a wish list
- Delivery drifts toward the urgent

**Prioritization is the connective tissue** that ensures:
- Strategy defines direction → Prioritization focuses energy
- Prioritization focuses energy → Planning structures commitments  
- Planning structures commitments → Delivery generates results
- Delivery generates results → Feedback refines Strategy and Prioritization

## The Flow

\`\`\`
Strategy defines direction 
  ↓
Prioritization focuses energy (THE SPINE)
  ↓
Planning structures commitments
  ↓
Delivery generates results and learning
  ↓
Feedback loop back to Strategy & Prioritization
\`\`\`

## How to Use This Framework

### As an Organizing Lens

Use the cascade to ask:
- "Which phase are we actually in?"
- "Which scope (individual or organization) is this about?"
- "Are all four phases connected, or is there a gap?"

### As a Diagnostic Tool

Look for breakdowns:
- **Strategy-Prioritization gap**: Clear strategy, but no mechanism to focus effort
- **Prioritization-Planning gap**: Clear priorities, but plans don't reflect them
- **Planning-Delivery gap**: Great plans, poor execution
- **Delivery-Strategy gap**: Executing well, but not learning/adapting

### As a Communication Framework

When discussing work, be explicit:
- "We need to clarify our strategy (phase) at the org level (scope)"
- "Our team prioritization (phase, org scope) isn't aligned with individual priorities (individual scope)"
- "We're great at delivery, but weak at feeding learning back into strategy"

## Where Other Frameworks Fit

**All prioritization frameworks and models operate within the Prioritization phase of this cascade.**

Examples:
- DEGAP (Decide, Engage, Gather, Arrange, Prioritize) → Lives in the Prioritization phase
- Capability Model (12C/4F/2O) → Assesses effectiveness across all four phases
- Six Kinds of Items → What gets prioritized within the Prioritization phase

## Why This Matters

Without this organizing lens:
- Work fragments into disconnected activities
- Teams optimize locally without strategic alignment
- Planning and delivery feel disconnected from "what matters"
- People work hard but don't see progress on important goals

With this organizing lens:
- Every piece of work has a clear home (phase + scope)
- The connection from intent to impact is visible
- Gaps and breakdowns become obvious
- Teams can diagnose and fix systemic issues

## The Meta-Insight

**The cascade itself is a dimension** (an organizing axis), not a framework. It combines with the Scope dimension to create a master organizing matrix. This is why it sits at the highest level—it's the lens through which all other frameworks are viewed.`
  },
  
  // ===== RELEVANCE FRAMEWORK =====
  relevance: {
    what: 'A 4×2 organizing matrix that shows how all work flows through four phases (Strategy, Prioritization, Planning, Delivery) across two scopes (Individual, Organization/Group), with Prioritization as the spine connecting intent to impact.',
    
    why: 'Most organizations and individuals struggle to connect strategic intent with actual results because work exists in scattered silos with no clear through-line. Without a master organizing lens, strategy stays theoretical, priorities drift, planning becomes disconnected, and delivery doesn\'t feed back into learning. This framework makes the invisible visible—showing exactly where work lives, how phases connect, and where breakdowns occur. Prioritization becomes elevated from a periodic activity to the critical spine linking all four phases.',
    
    how: 'Use the 4×2 matrix to position any piece of work by asking two questions: "Which phase am I in?" (Strategy, Prioritization, Planning, or Delivery) and "Which scope?" (Individual or Organization/Group). Then check the connections—is your current phase properly informed by the upstream phase and properly informing the downstream phase? Most importantly, verify that Prioritization is functioning as the spine: connecting Strategy to Planning, ensuring Planning reflects true priorities, and feeding Delivery results back into Strategy refinement.'
  },
  
  // ===== VISUAL LANGUAGE =====
  visual: {
    primaryDiagram: {
      type: 'matrix',
      title: 'Strategy-Delivery Cascade Matrix (4×2)',
      description: '4 phases (Strategy, Prioritization, Planning, Delivery) across 2 scopes (Individual, Organization/Group)',
      interactive: true,
      altText: 'Matrix showing the intersection of four work phases and two scope levels'
    },
    icon: '🎯',
    color: '#f59e0b', // amber-500
    metaphor: {
      family: 'journey',
      phrase: 'The cascade from intent to impact',
      visualCues: ['cascade flow arrows', 'spine connector', 'feedback loops', 'matrix grid']
    },
    onBrandElements: {
      backgroundColor: '#fffbeb', // amber-50
      accentColor: '#f59e0b',     // amber-500
      borderStyle: '2px solid #fbbf24', // amber-400
      shadowStyle: '0 4px 6px rgba(245, 158, 11, 0.1)'
    }
  },

  // ===== STRUCTURED RELATIONSHIPS =====
  relationships: {
    precededBy: [],
    followedBy: [],
    partOf: [],
    relatedTo: [
      { 
        frameworkId: 'pf.strategy_delivery.phases', 
        relationshipType: 'extends',
        description: 'Uses the 4-phase cascade as one organizing dimension' 
      },
      { 
        frameworkId: 'pf.framework.diagnostic.scope.levels.2level', 
        relationshipType: 'extends',
        description: 'Uses 2-level scope model as second organizing dimension' 
      },
      { 
        frameworkId: 'pf.degap.stages', 
        relationshipType: 'complements',
        description: 'DEGAP lives within the Prioritization phase of the cascade' 
      },
      { 
        frameworkId: 'pf.capability.model', 
        relationshipType: 'complements',
        description: 'Capabilities apply across all four phases of the cascade' 
      }
    ],
    usedIn: [
      {
        frameworkId: 'pf.framework.diagnostic',
        purpose: 'Provides the master organizing lens for diagnostic assessment'
      }
    ]
  },
  
  // ===== SCOPE & APPLICABILITY =====
  scope: {
    levels: ['individual', 'team', 'organization'],
    contexts: [
      'Setting organizational strategy and priorities',
      'Managing personal effectiveness and career development',
      'Diagnosing organizational breakdowns',
      'Aligning team and individual work',
      'Teaching prioritization and execution excellence'
    ]
  },
  
  // ===== PRACTICAL RESOURCES =====
  resources: {
    templates: [],
    facilitationGuides: [],
    instructions: {
      individual: 'Map your current work to the 4 phases (Strategy, Prioritization, Planning, Delivery) and verify alignment between personal and organizational priorities.',
      team: 'Use the cascade as a diagnostic tool to identify gaps between phases and ensure prioritization is functioning as the connecting spine.'
    },
    criticalCallouts: [],
    media: []
  },
  
  // ===== LEARNING INTEGRATION =====
  learning: {
    frameworkType: 'clarity-alignment',
    difficulty: 3,
    timeToLearn: 60,
    prerequisites: []
  },
  
  // ===== QUALITY & MISSTEPS =====
  quality: {
    checklist: {
      items: [
        {
          criterion: 'All four phases are clearly defined',
          passFail: true
        },
        {
          criterion: 'Prioritization is functioning as the spine',
          passFail: true
        },
        {
          criterion: 'Individual and organizational scopes are explicitly aligned',
          passFail: true
        }
      ]
    },
    commonMissteps: {
      missteps: [
        {
          mistake: 'Skipping prioritization phase',
          consequence: 'Strategy never connects to execution',
          fix: 'Treat prioritization as the essential spine - never skip it'
        },
        {
          mistake: 'Conflating individual and organizational scope',
          consequence: 'Misalignment and frustration',
          fix: 'Explicitly map both scopes and understand where they align and diverge'
        }
      ]
    }
  },
  
  // ===== METADATA =====
  metadata: {
    createdDate: '2025-01-15',
    lastUpdated: '2025-01-15',
    version: '1.0',
    status: 'published',
    contributors: ['Peak Priorities Team'],
    tags: ['master-framework', 'cascade', 'organizing-lens', 'strategy', 'prioritization', 'planning', 'delivery', 'scope', 'dimensions']
  }
};

